import { useState, useRef, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useGoogleMaps } from '../../hooks/useGoogleMaps'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import MembershipPlansEditor from '../../components/gyms/MembershipPlansEditor'
import axios from 'axios'
import { toast } from 'react-toastify'

const EditGym = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { user, hasPermission } = useAuth()
  const { isLoaded, error: mapsError, initializeAutocomplete, clearAutocomplete } = useGoogleMaps()
  const searchInputRef = useRef(null)

  const [loading, setLoading] = useState(false)
  const [fetchingGym, setFetchingGym] = useState(true)
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    latitude: '',
    longitude: '',
    gmbUrl: '',
    ownerName: '',
    ownerContact: '',
    expectedVisitDate: '',
    stage: '',
    totalSqft: '',
    ageOfGym: '',
    monthlyRent: '',
    cto: '',
    activeClients: '',
    membershipPlans: [],
    proposedAmount: '',
    partnershipReceivingAmount: ''
  })

  // Check if user has permission to edit gyms
  useEffect(() => {
    if (!hasPermission(4)) {
      toast.error('Access denied: Insufficient permissions')
      navigate('/gyms')
    }
  }, [user, hasPermission, navigate])

  // Fetch gym details
  useEffect(() => {
    fetchGymDetails()
  }, [id])

  const fetchGymDetails = async () => {
    try {
      setFetchingGym(true)
      const response = await axios.get(`/api/gyms/${id}`)
      const gym = response.data.gym

      // Populate form with existing gym data
      setFormData({
        name: gym.name || '',
        address: gym.address || '',
        city: gym.location || '',
        state: gym.state || '',
        pinCode: gym.pinCode || '',
        latitude: gym.gps_lat ? gym.gps_lat.toString() : '',
        longitude: gym.gps_lng ? gym.gps_lng.toString() : '',
        gmbUrl: gym.google_maps_url || '',
        ownerName: gym.contact_person || '',
        ownerContact: gym.ownerContact || '',
        expectedVisitDate: gym.expectedVisitDate ? new Date(gym.expectedVisitDate).toISOString().split('T')[0] : '',
        stage: gym.stage || '',
        totalSqft: gym.totalSqft != null ? gym.totalSqft.toString() : '',
        ageOfGym: gym.ageOfGym != null ? gym.ageOfGym.toString() : '',
        monthlyRent: gym.monthlyRent != null ? gym.monthlyRent.toString() : '',
        cto: gym.cto != null ? gym.cto.toString() : '',
        activeClients: gym.activeClients != null ? gym.activeClients.toString() : '',
        membershipPlans: gym.membershipPlans || [],
        proposedAmount: gym.proposedAmount != null ? gym.proposedAmount.toString() : '',
        partnershipReceivingAmount: gym.partnershipReceivingAmount != null ? gym.partnershipReceivingAmount.toString() : ''
      })
    } catch (error) {
      console.error('Error fetching gym details:', error)
      toast.error('Failed to load gym details')
      navigate('/gyms')
    } finally {
      setFetchingGym(false)
    }
  }

  // Initialize Google Maps autocomplete
  useEffect(() => {
    if (isLoaded && searchInputRef.current && !mapsError) {
      initializeAutocomplete(searchInputRef.current, handlePlaceSelect)

      return () => {
        clearAutocomplete()
      }
    }
  }, [isLoaded, mapsError])

  // Handle place selection from Google Maps
  const handlePlaceSelect = (placeData) => {
    // Generate Google Maps Location URL with gym name and coordinates
    const gymName = encodeURIComponent(placeData.name || '')
    const mapsUrl = `https://maps.google.com/?q=${gymName}+${placeData.latitude},${placeData.longitude}`

    setFormData(prev => ({
      ...prev,
      name: placeData.name || prev.name,
      address: placeData.address || prev.address,
      city: placeData.city || prev.city,
      state: placeData.state || prev.state,
      pinCode: placeData.pinCode || prev.pinCode,
      latitude: placeData.latitude?.toString() || '',
      longitude: placeData.longitude?.toString() || '',
      gmbUrl: mapsUrl
    }))

    // Clear validation errors for auto-filled fields
    setErrors(prev => ({
      ...prev,
      name: '',
      address: '',
      city: '',
      state: '',
      pinCode: ''
    }))

    // Mark auto-filled fields as touched
    setTouched(prev => ({
      ...prev,
      name: true,
      address: true,
      city: true,
      state: true,
      pinCode: true
    }))

    toast.success('Gym details updated from Google Maps!')
  }

  // Validation rules
  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value?.trim()) return 'Gym name is required'
        if (value.trim().length < 2) return 'Gym name must be at least 2 characters'
        return ''
      case 'address':
        if (!value?.trim()) return 'Address is required'
        return ''
      case 'city':
        if (!value?.trim()) return 'City is required'
        return ''
      case 'state':
        if (!value?.trim()) return 'State is required'
        return ''
      case 'pinCode':
        if (!value?.trim()) return 'Pin code is required'
        if (!/^[0-9]{6}$/.test(value)) {
          return 'Pin code must be exactly 6 digits'
        }
        return ''
      case 'ownerName':
        if (!value?.trim()) return 'Owner name is required'
        return ''
      case 'ownerContact':
        if (!value?.trim()) return 'Owner contact is required'
        if (!/^[0-9]{10}$/.test(value)) {
          return 'Contact number must be exactly 10 digits'
        }
        return ''
      default:
        return ''
    }
  }

  // Validate all fields
  const validateForm = () => {
    const newErrors = {}
    const requiredFields = ['name', 'address', 'city', 'state', 'pinCode', 'ownerName', 'ownerContact']

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field])
      if (error) newErrors[field] = error
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // For numeric-only fields, allow only numbers
    if (name === 'ownerContact') {
      // Only allow digits, max 10 characters
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10)
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }))
      // Validate on change if field was touched
      if (touched[name]) {
        const error = validateField(name, numericValue)
        setErrors(prev => ({
          ...prev,
          [name]: error
        }))
      }
      return
    }

    if (name === 'pinCode') {
      // Only allow digits, max 6 characters
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6)
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }))
      // Validate on change if field was touched
      if (touched[name]) {
        const error = validateField(name, numericValue)
        setErrors(prev => ({
          ...prev,
          [name]: error
        }))
      }
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Validate on change if field was touched
    if (touched[name]) {
      const error = validateField(name, value)
      setErrors(prev => ({
        ...prev,
        [name]: error
      }))
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target
    setTouched(prev => ({
      ...prev,
      [name]: true
    }))

    // Validate on blur
    const error = validateField(name, value)
    setErrors(prev => ({
      ...prev,
      [name]: error
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Mark all required fields as touched
    const requiredFields = ['name', 'address', 'city', 'state', 'pinCode', 'ownerName', 'ownerContact']
    const newTouched = {}
    requiredFields.forEach(field => {
      newTouched[field] = true
    })
    setTouched(prev => ({ ...prev, ...newTouched }))

    // Validate form
    if (!validateForm()) {
      toast.error('Please fill in all required fields correctly')
      return
    }

    setLoading(true)

    try {
      const gymData = {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pinCode: formData.pinCode,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        gmbUrl: formData.gmbUrl || null,
        contact_person: formData.ownerName,
        ownerContact: formData.ownerContact,
        expectedVisitDate: formData.expectedVisitDate ? new Date(formData.expectedVisitDate).toISOString() : null,
        ...(formData.totalSqft !== '' && { totalSqft: parseInt(formData.totalSqft) }),
        ...(formData.ageOfGym !== '' && { ageOfGym: parseInt(formData.ageOfGym) }),
        ...(formData.monthlyRent !== '' && { monthlyRent: parseFloat(formData.monthlyRent) }),
        ...(formData.cto !== '' && { cto: parseFloat(formData.cto) }),
        ...(formData.activeClients !== '' && { activeClients: parseInt(formData.activeClients) }),
        membershipPlans: formData.membershipPlans,
        ...(formData.proposedAmount !== '' && { proposedAmount: parseFloat(formData.proposedAmount) }),
        ...(formData.partnershipReceivingAmount !== '' && { partnershipReceivingAmount: parseFloat(formData.partnershipReceivingAmount) })
      }

      await axios.put(`/api/gyms/${id}`, gymData)

      toast.success('Gym updated successfully!')
      navigate(`/gyms/${id}`)
    } catch (error) {
      console.error('Error updating gym:', error)
      const message = error.response?.data?.error || 'Failed to update gym'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0]

  // Calculate form completion percentage
  const completionRequiredFields = ['name', 'address', 'city', 'state', 'pinCode', 'ownerName', 'ownerContact']
  const filledFields = completionRequiredFields.filter(field => formData[field]?.trim()).length
  const completionPercentage = Math.round((filledFields / completionRequiredFields.length) * 100)

  if (fetchingGym) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading gym details...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header Card */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-t-2xl px-8 py-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Edit Gym</h1>
                <p className="text-red-100 text-sm mt-1">
                  Update gym information and details
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/gyms/${id}`)}
              className="flex items-center px-4 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-lg transition-all duration-200 border border-white/20"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Gym
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm text-red-100 mb-2">
              <span>Form Completion</span>
              <span className="font-semibold text-white">{completionPercentage}%</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-500 ease-out"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-b-2xl shadow-xl">
          <form onSubmit={handleSubmit}>
            {/* Gym Details Section */}
            <div className="p-8 border-b border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Gym Details</h2>
                  <p className="text-sm text-gray-500">Basic information about the gym</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Google Maps Search */}
                <div className="md:col-span-2">
                  <label htmlFor="gymSearch" className="block text-sm font-medium text-gray-700 mb-2">
                    Search for Gym
                    <span className="ml-2 text-xs font-normal text-gray-400">(Auto-fill details from Google)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      id="gymSearch"
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                      placeholder={
                        mapsError
                          ? "Google Maps unavailable - please enter details manually"
                          : isLoaded
                            ? "Search for gym (e.g., Gold's Gym Mumbai)"
                            : "Loading Google Maps..."
                      }
                      disabled={!isLoaded || mapsError}
                    />
                    {!isLoaded && !mapsError && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </div>
                  {mapsError ? (
                    <p className="mt-2 text-xs text-amber-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Google Maps unavailable. Please enter gym details manually below.
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500 flex items-center">
                      <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Search and select a gym to auto-fill address details
                    </p>
                  )}
                </div>

                {/* Gym Name */}
                <div className="md:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Gym Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.name && touched.name ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.name && touched.name
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., FitZone Premium Gym"
                    />
                    {formData.name && !errors.name && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.name && touched.name && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.address && touched.address ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="address"
                      id="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.address && touched.address
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., 123 Main Street, Sector 5"
                    />
                    {formData.address && !errors.address && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.address && touched.address && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.address}
                    </p>
                  )}
                </div>

                {/* City */}
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.city && touched.city ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="city"
                      id="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.city && touched.city
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., Mumbai"
                    />
                    {formData.city && !errors.city && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.city && touched.city && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.city}
                    </p>
                  )}
                </div>

                {/* State */}
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.state && touched.state ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="state"
                      id="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.state && touched.state
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., Maharashtra"
                    />
                    {formData.state && !errors.state && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.state && touched.state && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.state}
                    </p>
                  )}
                </div>

                {/* Pin Code */}
                <div>
                  <label htmlFor="pinCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Code <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.pinCode && touched.pinCode ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="pinCode"
                      id="pinCode"
                      value={formData.pinCode}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={6}
                      inputMode="numeric"
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.pinCode && touched.pinCode
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., 400001"
                    />
                    {formData.pinCode && !errors.pinCode && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.pinCode && touched.pinCode && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.pinCode}
                    </p>
                  )}
                </div>

                {/* Expected Visit Date */}
                <div>
                  <label htmlFor="expectedVisitDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Visit Date
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="date"
                      name="expectedVisitDate"
                      id="expectedVisitDate"
                      value={formData.expectedVisitDate}
                      onChange={handleInputChange}
                      min={today}
                      className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500 flex items-center">
                    <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule your next site visit to this gym
                  </p>
                </div>
              </div>
            </div>

            {/* Owner Details Section */}
            <div className="p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Owner Information</h2>
                  <p className="text-sm text-gray-500">Contact details of the gym owner</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Owner Name */}
                <div>
                  <label htmlFor="ownerName" className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.ownerName && touched.ownerName ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="ownerName"
                      id="ownerName"
                      value={formData.ownerName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.ownerName && touched.ownerName
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="e.g., Rajesh Kumar"
                    />
                    {formData.ownerName && !errors.ownerName && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.ownerName && touched.ownerName && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.ownerName}
                    </p>
                  )}
                </div>

                {/* Owner Contact Number */}
                <div>
                  <label htmlFor="ownerContact" className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-400">(10 digits only)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className={`w-5 h-5 ${errors.ownerContact && touched.ownerContact ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      name="ownerContact"
                      id="ownerContact"
                      value={formData.ownerContact}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      maxLength={10}
                      inputMode="numeric"
                      className={`block w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        errors.ownerContact && touched.ownerContact
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500 hover:border-gray-400'
                      }`}
                      placeholder="9876543210"
                    />
                    {formData.ownerContact && !errors.ownerContact && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.ownerContact && touched.ownerContact && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.ownerContact}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Site Visit Data Section */}
            <div className="p-8 border-t border-gray-100">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Site Visit Data</h2>
                  <p className="text-sm text-gray-500">Details collected during the site visit</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Total Sqft */}
                <div>
                  <label htmlFor="totalSqft" className="block text-sm font-medium text-gray-700 mb-2">
                    Total Area (sqft)
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="totalSqft"
                    id="totalSqft"
                    value={formData.totalSqft}
                    onChange={handleInputChange}
                    min="0"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    placeholder="e.g., 2500"
                  />
                </div>

                {/* Age of Gym */}
                <div>
                  <label htmlFor="ageOfGym" className="block text-sm font-medium text-gray-700 mb-2">
                    Age of Gym (years)
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="ageOfGym"
                    id="ageOfGym"
                    value={formData.ageOfGym}
                    onChange={handleInputChange}
                    min="0"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    placeholder="e.g., 5"
                  />
                </div>

                {/* Monthly Rent */}
                <div>
                  <label htmlFor="monthlyRent" className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Rent (₹)
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="monthlyRent"
                    id="monthlyRent"
                    value={formData.monthlyRent}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    placeholder="e.g., 50000"
                  />
                </div>

                {/* CTO */}
                <div>
                  <label htmlFor="cto" className="block text-sm font-medium text-gray-700 mb-2">
                    CTO (₹)
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="cto"
                    id="cto"
                    value={formData.cto}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    placeholder="e.g., 120000"
                  />
                </div>

                {/* Active Clients */}
                <div>
                  <label htmlFor="activeClients" className="block text-sm font-medium text-gray-700 mb-2">
                    Active Clients
                    <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    name="activeClients"
                    id="activeClients"
                    value={formData.activeClients}
                    onChange={handleInputChange}
                    min="0"
                    className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                    placeholder="e.g., 150"
                  />
                </div>
              </div>

              {/* Membership Plans */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Membership Plans
                  <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                </label>
                <MembershipPlansEditor
                  plans={formData.membershipPlans}
                  onChange={(plans) => setFormData(prev => ({ ...prev, membershipPlans: plans }))}
                />
              </div>
            </div>

            {/* Proposal Section */}
            {['Proposal Sent', 'Agreement Signed', 'Facility Work', 'Post Reki Approved', 'Go-Live Ready', 'Go-Live Done'].includes(formData.stage) && (
              <div className="p-8 border-t border-gray-100">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Proposal</h2>
                    <p className="text-sm text-gray-500">Financial terms for the partnership</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Proposed Amount */}
                  <div>
                    <label htmlFor="proposedAmount" className="block text-sm font-medium text-gray-700 mb-2">
                      Proposed Amount (₹)
                      <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      name="proposedAmount"
                      id="proposedAmount"
                      value={formData.proposedAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                      placeholder="e.g., 500000"
                    />
                  </div>

                  {/* Partnership Receiving Amount */}
                  <div>
                    <label htmlFor="partnershipReceivingAmount" className="block text-sm font-medium text-gray-700 mb-2">
                      Partnership Receiving Amount (₹)
                      <span className="ml-2 text-xs font-normal text-gray-400">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      name="partnershipReceivingAmount"
                      id="partnershipReceivingAmount"
                      value={formData.partnershipReceivingAmount}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 hover:border-gray-400 transition-all duration-200"
                      placeholder="e.g., 250000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="px-8 py-6 bg-gray-50 rounded-b-2xl border-t border-gray-100">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <p className="text-sm text-gray-500 flex items-center">
                  <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Fields marked with <span className="text-red-500 mx-1">*</span> are required
                </p>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/gyms/${id}`)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 shadow-sm"
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Update Gym
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Help Card */}
        <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Quick Tip</h3>
              <p className="mt-1 text-sm text-blue-700">
                Use the Google Maps search to quickly update gym details. Simply search for the gym and select it from the suggestions to auto-fill address information.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditGym
